import { z } from "zod";
import { validateSafeName } from "@/lib/validation/names";

function safeNameSchema(maxLength: number, field: string) {
  return z.string().superRefine((val, ctx) => {
    const result = validateSafeName(val, maxLength, field);
    if (!result.ok) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.error });
    }
  });
}

export const RangeMetaSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  game_type_id: z.string().uuid().nullable(),
  table_format_id: z.string().uuid().nullable(),
  hero_position_id: z.string().uuid().nullable(),
  spot_id: z.string().uuid().nullable(),
  villain_position_id: z.string().uuid().nullable(),
  stack_depth_bb: z.number().int().min(1).max(500).nullable(),
  notes: z.string().nullable(),
  owner_id: z.string().uuid().nullable(),
  description: z.string().nullable(),
  visibility: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const PaletteActionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/),
  order: z.number().int().min(0).max(7),
  is_fold: z.boolean(),
});


// Library tree — flat row from get_library_tree(); the client nests via parent_id.
// `node_position` instead of `position` because POSITION is reserved in the
// RETURNS TABLE grammar — see get_library_tree in 20260426000002_rpc_functions.sql.
export const LibraryRowSchema = z.object({
  id: z.string().uuid(),
  node_type: z.enum(["folder", "pack", "range"]),
  name: z.string(),
  parent_id: z.string().uuid().nullable(),
  node_position: z.number().int(),
  path: z.string(),
  color: z.string().nullable().default(null),
  range_id: z.string().uuid().nullable(),
  range_title: z.string().nullable(),
  range_game_type_id: z.string().uuid().nullable(),
  range_table_format_id: z.string().uuid().nullable(),
  range_hero_position_id: z.string().uuid().nullable(),
  range_spot_id: z.string().uuid().nullable(),
  range_villain_position_id: z.string().uuid().nullable(),
  range_stack_depth_bb: z.number().int().nullable(),
  range_notes: z.string().nullable(),
  owner_id: z.string().uuid().nullable().default(null),
  visibility: z.string().nullable().default(null),
  owner_email: z.string().nullable().default(null),
  owner_username: z.string().nullable().default(null),
  node_game_type_id: z.string().uuid().nullable().default(null),
  node_table_format_id: z.string().uuid().nullable().default(null),
  node_stack_depth_bb: z.number().int().nullable().default(null),
  node_rake: z.string().nullable().default(null),
});
export type LibraryRow = z.infer<typeof LibraryRowSchema>;

export const PackTreeRowSchema = z.object({
  id: z.string().uuid(),
  parent_node_id: z.string().uuid().nullable(),
  action_segment: z.string().nullable(),
  action_path: z.string(),
  active_position: z.string(),
  range_id: z.string().uuid().nullable(),
  range_title: z.string().nullable(),
  range_game_type_id: z.string().uuid().nullable(),
  range_table_format_id: z.string().uuid().nullable(),
  range_hero_position_id: z.string().uuid().nullable(),
  range_spot_id: z.string().uuid().nullable(),
  range_stack_depth_bb: z.number().int().nullable(),
  node_position: z.number().int(),
  range_thumbnail: z.array(z.string().nullable()).nullable().default(null),
});
export type PackTreeRow = z.infer<typeof PackTreeRowSchema>;

export const PackNodeRowSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  node_type: z.literal("pack"),
  visibility: z.string().nullable(),
  owner_id: z.string().uuid().nullable(),
  rake: z.string().nullable(),
  game_type_code: z.string().nullable(),
  table_format_code: z.string().nullable(),
  stack_depth_bb: z.number().int().nullable().default(null),
});
export type PackNodeRow = z.infer<typeof PackNodeRowSchema>;

// Profiles
export const ProfileSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  username: z.string().min(3).max(20),
  display_name: z.string().nullable(),
  role: z.enum(["user", "admin"]),
  subscription_plan_id: z.string().uuid().nullable(),
  created_at: z.string(),
});
export type ProfileParsed = z.infer<typeof ProfileSchema>;


// Imports
export const ImportFormat = z.enum(["flopzilla", "gto_wizard"]);

export const ImportCellSchema = z.object({
  cell_index: z.number().int().min(0).max(168),
  weight: z.number().positive().max(1),
});

export const ImportApplyRequestSchema = z.object({
  raise_cells: z.array(ImportCellSchema).default([]),
  call_cells: z.array(ImportCellSchema).default([]),
  title: safeNameSchema(200, "Title"),
  parent_id: z.string().uuid().nullable().optional(),
});

export const AdminLabelSchema = safeNameSchema(100, "Label");

// HRC strategy import
const HrcActionType = z.enum(["F", "C", "R"]);

const HrcImportActionSchema = z.object({
  key: z.string(),
  label: z.string().max(30),
  color: z.string(),
  type: HrcActionType,
});

const HrcImportNodeSchema = z.object({
  position: z.string(),
  actionPath: z.string(),
  actionSegment: z.string(),
  parentPath: z.string().nullable(),
  actions: z.array(z.object({ key: z.string(), type: HrcActionType })),
  cells: z.array(z.tuple([
    z.number().int().min(0).max(168),
    z.record(z.string(), z.number().min(0).max(1)),
  ])),
});

export const HrcStrategyImportSchema = z.object({
  packName: safeNameSchema(200, "packName"),
  parentId: z.string().uuid().nullable(),
  gameTypeId: z.string().uuid(),
  tableFormatId: z.string().uuid(),
  stackDepthBb: z.number().int().min(1).max(500).nullable(),
  rake: z.string().max(50).nullable(),
  actions: z.array(HrcImportActionSchema).max(10),
  nodes: z.array(HrcImportNodeSchema).max(200),
});

export const SharedTreeResponseSchema = z.object({
  node_type: z.string(),
  library_nodes: z.array(z.unknown()).default([]),
  pack_tree: z.array(z.unknown()).optional(),
});
